import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { AllowedWebsiteTemplateSystemIdSchema } from '@pillar-portal/api-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { allowedWebsiteTemplatesApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import type {
    AllowedWebsiteTemplateMutationResponse,
    AllowedWebsiteTemplateListItem,
    AllowedWebsiteTemplateTransfer,
    RbsWebsite,
    UpdateAllowedWebsiteTemplateRequest,
    UrlPatternsRule,
    UnmanagedAllowedWebsite,
} from '@pillar-portal/api-types';
import {
    AllowedWebsiteTemplateEditor,
    AllowedWebsiteTemplateValidationPanel,
    buildAllowedWebsiteTemplateEditorStateFromTemplate,
    buildAllowedWebsiteTemplateEditorStateFromTransfer,
    buildAllowedWebsiteTemplateUpdateRequest,
    getAllowedWebsiteTemplateValidationIssues,
    type AllowedWebsiteTemplateEditorState,
    type AllowedWebsiteTemplateValidationIssue,
} from '../AllowedWebsiteTemplateEditor';
import {
    AllowedWebsiteTemplateImportButton,
    downloadAllowedWebsiteTemplateTransfer,
} from '../AllowedWebsiteTemplateTransferControls';
import { formatAllowedWebsiteTemplateSystemName } from '../allowedWebsiteTemplateSystemDisplay';

const TemplateUuidParamSchema: z.ZodType<string> = z.string().uuid();

function formatBoolean(value: boolean): string {
    return value ? 'Yes' : 'No';
}

function renderRules(rules: ReadonlyArray<UrlPatternsRule>): string {
    if (rules.length === 0) {
        return 'No explicit rules configured.';
    }

    return rules.map((rule) => `${rule.action}: ${rule.urlPatterns.join(', ')}`).join('; ');
}

function WebsitePolicyDetails(props: { readonly websiteConfig: UnmanagedAllowedWebsite }) {
    const websiteConfig: UnmanagedAllowedWebsite = props.websiteConfig;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Website policy</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                <p>
                    <span className="font-medium">Website name:</span> {websiteConfig.name}
                </p>
                <p className="break-all">
                    <span className="font-medium">Website URL:</span> {websiteConfig.url}
                </p>
                <p>
                    <span className="font-medium">Session persistence:</span>{' '}
                    {websiteConfig.sessionPersistence}
                </p>
                <p>
                    <span className="font-medium">Promote session cookies:</span>{' '}
                    {formatBoolean(websiteConfig.promoteSessionCookies)}
                </p>
                <p className="break-all md:col-span-2">
                    <span className="font-medium">Allowed URL patterns:</span>{' '}
                    {websiteConfig.allowedUrlPatterns.join(', ')}
                </p>
                <p>
                    <span className="font-medium">Fetch default action:</span>{' '}
                    {websiteConfig.fetchDefaultAction}
                </p>
                <p>
                    <span className="font-medium">Iframe default action:</span>{' '}
                    {websiteConfig.iframeDefaultAction}
                </p>
                <p className="break-all md:col-span-2">
                    <span className="font-medium">Fetch URL pattern rules:</span>{' '}
                    {renderRules(websiteConfig.urlPatternsRules)}
                </p>
                <p className="break-all md:col-span-2">
                    <span className="font-medium">Iframe URL pattern rules:</span>{' '}
                    {renderRules(websiteConfig.iframeUrlPatterns)}
                </p>
            </CardContent>
        </Card>
    );
}

function TemplateMetadataDetails(props: { readonly template: AllowedWebsiteTemplateListItem }) {
    return (
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <CardTitle className="text-base">Template</CardTitle>
                    <StatusBadge status={props.template.status} />
                </div>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                <p>
                    <span className="font-medium">Template name:</span> {props.template.name}
                </p>
                <p>
                    <span className="font-medium">Allowed website:</span>{' '}
                    {formatAllowedWebsiteTemplateSystemName(props.template.ipx_system_id)}
                </p>
                <p className="break-all md:col-span-2">
                    <span className="font-medium">Template UUID:</span>{' '}
                    {props.template.allowed_website_template_uuid}
                </p>
            </CardContent>
        </Card>
    );
}

function UsageDetails(props: { readonly template: AllowedWebsiteTemplateListItem }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Merchant usage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                <p>
                    <span className="font-medium">Active linked merchants:</span>{' '}
                    {props.template.blast_radius.active_merchant_count}
                </p>
                <p>
                    Template edits affect linked merchants when their app configuration is next
                    refreshed.
                </p>
            </CardContent>
        </Card>
    );
}

function CapabilityDetails(props: { readonly template: AllowedWebsiteTemplateListItem }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Capabilities</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm md:grid-cols-3">
                <p>Edit: {formatBoolean(props.template.capabilities.can_edit)}</p>
                <p>Disable: {formatBoolean(props.template.capabilities.can_disable)}</p>
                <p>Enable: {formatBoolean(props.template.capabilities.can_enable)}</p>
                <p>Delete: {formatBoolean(props.template.capabilities.can_delete)}</p>
                <p>Use: {formatBoolean(props.template.capabilities.can_use)}</p>
                <p>Audit: {formatBoolean(props.template.capabilities.can_audit)}</p>
            </CardContent>
        </Card>
    );
}

interface AllowedWebsiteTemplateEditBaseline {
    readonly websiteConfig: UnmanagedAllowedWebsite;
    readonly activeMerchantCount: number;
}

export default function AllowedWebsiteTemplateDetailPage() {
    const params = useParams();
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const [editState, setEditState] = useState<AllowedWebsiteTemplateEditorState | null>(null);
    const [editBaseline, setEditBaseline] = useState<AllowedWebsiteTemplateEditBaseline | null>(
        null
    );
    const [pendingConfirmedRequest, setPendingConfirmedRequest] =
        useState<UpdateAllowedWebsiteTemplateRequest | null>(null);
    const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [importedRbsWebsite, setImportedRbsWebsite] = useState<RbsWebsite | null | undefined>(
        undefined
    );
    const templateUuidParseResult = TemplateUuidParamSchema.safeParse(params.templateUuid);
    const templateUuid: string = templateUuidParseResult.success
        ? templateUuidParseResult.data
        : '';
    const templateQueryKey = ['allowedWebsiteTemplate', templateUuid] as const;

    const templateQuery = useQuery({
        queryKey: templateQueryKey,
        enabled: templateUuidParseResult.success,
        queryFn: async () => {
            if (!templateUuidParseResult.success) {
                throw new Error('Invalid template UUID');
            }
            return allowedWebsiteTemplatesApi.getTemplate(templateUuid);
        },
    });

    const template: AllowedWebsiteTemplateListItem | undefined = templateQuery.data?.template;
    const validationIssues: ReadonlyArray<AllowedWebsiteTemplateValidationIssue> = useMemo(
        () => (editState === null ? [] : getAllowedWebsiteTemplateValidationIssues(editState)),
        [editState]
    );
    const activeMerchantCount: number = template?.blast_radius.active_merchant_count ?? 0;

    const updateMutation = useMutation({
        mutationFn: async (request: UpdateAllowedWebsiteTemplateRequest) => {
            return allowedWebsiteTemplatesApi.updateTemplate(templateUuid, request);
        },
        onSuccess: (response: AllowedWebsiteTemplateMutationResponse) => {
            queryClient.setQueryData(templateQueryKey, response);
            setEditState(null);
            setEditBaseline(null);
            setPendingConfirmedRequest(null);
            setImportedRbsWebsite(undefined);
            setErrorMessage(null);
        },
        onError: (error) => {
            setErrorMessage(error instanceof Error ? error.message : 'Update failed');
        },
    });

    const enableMutation = useMutation({
        mutationFn: async () =>
            allowedWebsiteTemplatesApi.enableTemplate({
                allowed_website_template_uuid: templateUuid,
            }),
        onSuccess: (response: AllowedWebsiteTemplateMutationResponse) => {
            queryClient.setQueryData(templateQueryKey, response);
            setErrorMessage(null);
        },
        onError: (error) => {
            setErrorMessage(error instanceof Error ? error.message : 'Enable failed');
        },
    });

    const disableMutation = useMutation({
        mutationFn: async () =>
            allowedWebsiteTemplatesApi.disableTemplate({
                allowed_website_template_uuid: templateUuid,
            }),
        onSuccess: (response: AllowedWebsiteTemplateMutationResponse) => {
            queryClient.setQueryData(templateQueryKey, response);
            setErrorMessage(null);
        },
        onError: (error) => {
            setErrorMessage(error instanceof Error ? error.message : 'Disable failed');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () =>
            allowedWebsiteTemplatesApi.deleteTemplate({
                allowed_website_template_uuid: templateUuid,
            }),
        onSuccess: () => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: '/allowed-website-templates',
                    title: 'Allowed Website Templates',
                },
            });
        },
        onError: (error) => {
            setErrorMessage(error instanceof Error ? error.message : 'Delete failed');
        },
    });

    function startEdit(nextTemplate: AllowedWebsiteTemplateListItem): void {
        setEditState(buildAllowedWebsiteTemplateEditorStateFromTemplate(nextTemplate));
        setEditBaseline({
            websiteConfig: nextTemplate.website_config,
            activeMerchantCount: nextTemplate.blast_radius.active_merchant_count,
        });
        setPendingConfirmedRequest(null);
        setImportedRbsWebsite(undefined);
        setIsDeleteConfirmationVisible(false);
        setErrorMessage(null);
    }

    function handleSaveEdit(): void {
        if (editState === null || editBaseline === null || validationIssues.length > 0) {
            return;
        }

        let request: UpdateAllowedWebsiteTemplateRequest;
        try {
            request = buildAllowedWebsiteTemplateUpdateRequest(
                editState,
                editBaseline.websiteConfig,
                editBaseline.activeMerchantCount
            );
            if (importedRbsWebsite !== undefined) {
                request = {
                    ...request,
                    rbs_website: importedRbsWebsite,
                };
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Validation failed');
            return;
        }

        if (editBaseline.activeMerchantCount > 0) {
            setPendingConfirmedRequest(request);
            return;
        }
        updateMutation.mutate(request);
    }

    const canDeleteTemplate: boolean =
        template?.capabilities.can_delete === true && activeMerchantCount === 0;
    const deleteDisabledReason: string | undefined =
        template?.capabilities.can_delete !== true
            ? 'You do not have permission to delete this template.'
            : activeMerchantCount > 0
              ? `Cannot delete while ${activeMerchantCount} active merchants reference it.`
              : undefined;

    if (!templateUuidParseResult.success) {
        return (
            <div className="mx-auto max-w-4xl p-6">
                <Card className="border-red-300">
                    <CardHeader>
                        <CardTitle className="text-base text-red-700">
                            Invalid template UUID
                        </CardTitle>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-4xl p-6">
            <header className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <h1 className="text-2xl font-bold">
                    {template?.name ?? 'Allowed Website Template Details'}
                </h1>
                <div className="flex flex-wrap justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/allowed-website-templates',
                                    title: 'Allowed Website Templates',
                                },
                            });
                        }}
                    >
                        Back
                    </Button>
                    {template !== undefined ? (
                        editState === null ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const transfer: AllowedWebsiteTemplateTransfer = {
                                            ipx_system_id:
                                                AllowedWebsiteTemplateSystemIdSchema.parse(
                                                    template.ipx_system_id
                                                ),
                                            name: template.name,
                                            website_config: template.website_config,
                                            rbs_website: template.rbs_website,
                                        };
                                        try {
                                            downloadAllowedWebsiteTemplateTransfer(transfer);
                                            setErrorMessage(null);
                                        } catch (error) {
                                            setErrorMessage(
                                                error instanceof Error
                                                    ? error.message
                                                    : 'Export failed'
                                            );
                                        }
                                    }}
                                >
                                    Export
                                </Button>
                                <AllowedWebsiteTemplateImportButton
                                    disabled={!template.capabilities.can_edit}
                                    onImport={(transfer) => {
                                        setEditState(
                                            buildAllowedWebsiteTemplateEditorStateFromTransfer(
                                                transfer
                                            )
                                        );
                                        setEditBaseline({
                                            websiteConfig: template.website_config,
                                            activeMerchantCount:
                                                template.blast_radius.active_merchant_count,
                                        });
                                        setImportedRbsWebsite(transfer.rbs_website);
                                        setPendingConfirmedRequest(null);
                                        setIsDeleteConfirmationVisible(false);
                                        setErrorMessage(null);
                                    }}
                                    onError={setErrorMessage}
                                />
                                <span
                                    title={
                                        template.capabilities.can_edit
                                            ? undefined
                                            : 'You do not have permission to edit this template.'
                                    }
                                >
                                    <Button
                                        disabled={!template.capabilities.can_edit}
                                        onClick={() => startEdit(template)}
                                    >
                                        Edit
                                    </Button>
                                </span>
                                <span
                                    title={
                                        template.capabilities.can_disable
                                            ? undefined
                                            : 'This template cannot be disabled.'
                                    }
                                >
                                    <Button
                                        variant="outline"
                                        disabled={
                                            !template.capabilities.can_disable ||
                                            disableMutation.isPending
                                        }
                                        onClick={() => disableMutation.mutate()}
                                    >
                                        Disable
                                    </Button>
                                </span>
                                <span
                                    title={
                                        template.capabilities.can_enable
                                            ? undefined
                                            : 'This template cannot be enabled.'
                                    }
                                >
                                    <Button
                                        variant="outline"
                                        disabled={
                                            !template.capabilities.can_enable ||
                                            enableMutation.isPending
                                        }
                                        onClick={() => enableMutation.mutate()}
                                    >
                                        Enable
                                    </Button>
                                </span>
                                <span title={deleteDisabledReason}>
                                    <Button
                                        variant="destructive"
                                        disabled={!canDeleteTemplate || deleteMutation.isPending}
                                        onClick={() => setIsDeleteConfirmationVisible(true)}
                                    >
                                        Delete
                                    </Button>
                                </span>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setEditState(null);
                                        setEditBaseline(null);
                                        setPendingConfirmedRequest(null);
                                        setImportedRbsWebsite(undefined);
                                        setIsDeleteConfirmationVisible(false);
                                        setErrorMessage(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={
                                        validationIssues.length > 0 || updateMutation.isPending
                                    }
                                    onClick={handleSaveEdit}
                                >
                                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                            </>
                        )
                    ) : null}
                </div>
            </header>

            {templateQuery.isLoading ? (
                <Card>
                    <CardContent className="p-6 text-sm">
                        Loading allowed website template...
                    </CardContent>
                </Card>
            ) : templateQuery.error !== null ? (
                <Card className="border-red-300">
                    <CardHeader>
                        <CardTitle className="text-base text-red-700">
                            Failed to load template
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm">
                            {templateQuery.error instanceof Error
                                ? templateQuery.error.message
                                : 'Unknown error'}
                        </p>
                    </CardContent>
                </Card>
            ) : template !== undefined ? (
                <div className="space-y-4">
                    {errorMessage !== null ? (
                        <Card className="border-red-300">
                            <CardHeader>
                                <CardTitle className="text-base text-red-700">Error</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm">{errorMessage}</p>
                            </CardContent>
                        </Card>
                    ) : null}

                    {editState !== null ? (
                        <>
                            <AllowedWebsiteTemplateValidationPanel issues={validationIssues} />
                            {importedRbsWebsite !== undefined ? (
                                <Card>
                                    <CardContent className="p-4 text-sm">
                                        Imported template file loaded for explicit replacement of
                                        this template.{' '}
                                        {importedRbsWebsite === null
                                            ? 'The imported file contains no RBS workflows.'
                                            : 'The imported file includes its complete RBS workflow configuration.'}
                                    </CardContent>
                                </Card>
                            ) : null}
                            {pendingConfirmedRequest !== null ? (
                                <Card className="border-amber-300">
                                    <CardHeader>
                                        <CardTitle className="text-base">
                                            Confirm template update
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3 text-sm">
                                        <p>
                                            This template is linked to {activeMerchantCount} active
                                            merchants. Saving will affect those merchants on their
                                            next app configuration refresh.
                                        </p>
                                        <div className="flex justify-end gap-3">
                                            <Button
                                                variant="outline"
                                                onClick={() => setPendingConfirmedRequest(null)}
                                            >
                                                Review changes
                                            </Button>
                                            <Button
                                                disabled={updateMutation.isPending}
                                                onClick={() =>
                                                    updateMutation.mutate(pendingConfirmedRequest)
                                                }
                                            >
                                                Confirm Save
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ) : null}
                            <AllowedWebsiteTemplateEditor
                                state={editState}
                                onChange={(nextState) => {
                                    setEditState(nextState);
                                    setPendingConfirmedRequest(null);
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <TemplateMetadataDetails template={template} />
                            {activeMerchantCount > 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    Delete is blocked because {activeMerchantCount} active merchants
                                    reference this template.
                                </p>
                            ) : null}
                            {isDeleteConfirmationVisible ? (
                                <Card className="border-red-300">
                                    <CardHeader>
                                        <CardTitle className="text-base text-red-700">
                                            Confirm delete
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3 text-sm">
                                        <p>
                                            This permanently deletes the unused allowed website
                                            template.
                                        </p>
                                        <div className="flex justify-end gap-3">
                                            <Button
                                                variant="outline"
                                                onClick={() =>
                                                    setIsDeleteConfirmationVisible(false)
                                                }
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                disabled={deleteMutation.isPending}
                                                onClick={() => deleteMutation.mutate()}
                                            >
                                                Confirm Delete
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ) : null}
                            <UsageDetails template={template} />
                            <WebsitePolicyDetails websiteConfig={template.website_config} />
                            <CapabilityDetails template={template} />
                        </>
                    )}
                </div>
            ) : null}
        </div>
    );
}
